import React from 'react'

const DataAnal = () => {
    return (
        <div className='container'>
            <article class="cell large-7 xlarge-8 blog-post__article">
    <div class="blog-header">
      
        <h1 class="blog-title-primary">What Does a Data Analyst Do? Exploring the Day-to-Day of This Tech Career</h1>
    
    </div>

    <div class="blog-content">
      
        <p>You’ve heard the job title “data analyst” floating around lately and it sounds like an intriguing career. The
            idea of working with data and technology has piqued your curiosity, but what does a data analyst do all day?
        </p>
        <p>It’s one thing to daydream about a job—and another thing to perform those job duties every day. You need all
            the facts before you choose to pursue a new career. We did the digging to uncover what data analysts
            <em>really</em> do all day. Read on to discover the daily job duties that could be part of your future
            career.</p>
        <h2>A day in the life of a data analyst</h2>
        <p>Generally speaking, a data analyst will retrieve and gather data, organize it and use it to reach meaningful
            conclusions. “Data analysts’ work varies depending on the type of data that they’re working with (sales,
            social media, inventory, etc.) as well as the specific client project,” says Stephanie Pham, analyst for <a
                href="http://www.porternovelli.com/">Porter Novelli</a>.</p>
        <p>Companies in nearly every industry can benefit from the work of data analysts, from healthcare providers to
            retail stores to fast food chains. The insights that data analysts bring to an organization can be valuable
            to employers who want to know more about the needs of their consumer or end user.</p>
        <p>Regardless of which industry they work in, data analysts can expect to spend their time developing systems
            for collecting data and compiling their findings into reports that can help improve their company.</p>
        <p>Analysts can be involved in any part of the analysis process. In a data analyst role, you could be included
            in everything from setting up an analytics system to providing insights based on the data you collect—you
            may even be asked to train others in your data-collection system.</p>
        <p>Now that you have an idea of what data analysts do in general, you’re ready to dig into the specifics of life
            on the job as a data analyst.</p>
        <h2>What are some common data analyst responsibilities?</h2>
        <p>We enlisted some experts to help you get a sneak peek of the daily duties of a typical data analyst.</p>
        <h3>1. Producing reports</h3>
        <p>“As an analyst, I spend a significant amount of time producing and maintaining both internal and
            client-facing reports,” says Casey Pearson, marketing analyst at <a
                href="http://www.delphicdigital.com/">Delphic Digital</a>. Those reports give management insights about
            new trends on the horizon as well as areas the company may need to improve upon.</p>
        <p>Writing up a report isn’t as simple as throwing numbers onto a blank page and sending it to your manager.
            “Successful data analysts understand how to create narratives with data,” says Jess Kendra, manager of
            analytics at Porter Novelli. “To remain valuable, the reports, answers and insights that data analysis
            provides have to be understood by the next decision-maker, who frequently is not an analyst.”</p>
        <h3>2. Spotting patterns</h3>
        <p>The most effective data analysts are able to use data to tell a story. In order to produce a meaningful
            report, a data analyst first has to be able to see important patterns in the data. “At the base level, data
            is used to find trends and insights that we can use to make recommendations to our clients,” Pham says.</p>
        <p>Reporting in regular increments, such as weekly, monthly or quarterly, is important since it helps an analyst
            notice significant patterns. “They all contribute to an overarching time frame where we can see trends over
            time,” Pham adds.</p>
        <h3>3. Collaborating with others</h3>
        <p>Surprised to see this on the list? The word “analyst” might make you think of someone working apart from the
            rest of the company, but that’s far from the truth. The wide variety of data analyst roles and
            responsibilities means you’ll collaborate across many other departments in your organization including
            marketers, executives and salespeople. You’ll also likely collaborate closely with those who work in data
            science like data architects and database developers.</p>
        <p>Being able to communicate well is important. “Your success is dependent on your ability to work with
            people—the people you are gathering the research questions from, peers you collaborate with to execute the
            work and the people you deliver the final presentation to,” Kendra says.</p>
        <h3>4. Collecting data and setting up infrastructure</h3>
        <p>Perhaps the most technical aspect of an analyst’s job is collecting the data itself. This often means working
            together with web developers to optimize data collection, according to Pearson.</p>
        <p>Streamlining this data collection is key for data analysts. They work to develop routines that can be
            automated and easily modified for reuse in other areas. Analysts keep a handful of specialized software and
            tools in their arsenal to help them accomplish this.</p>
        <h2>Data Analyst vs. Data Scientist</h2>
        <p>With all that in mind, you might be wondering about another prominent data role—the data scientist. While
            it’s safe to assume there is some overlap in the type of work they do, there are significant differences
            between data analysts and data scientists.</p>
        <p>Since the role of a data scientist is relatively new and sometimes nebulous, those in the field have worked
            to define and differentiate it from that of the data analyst. Let’s break it down based on skills and job
            duties. </p>
        <p>Data analysts:<sup>1</sup></p>
        <ul>
            <li>Have moderate math and statistical skills</li>
            <li>Have a strong business acumen</li>
            <li>Have moderate computer science / coding skills</li>
            <li>Develop key performance indicators</li>
            <li>Create visualizations of the data</li>
            <li>Utilize business intelligence and analytics tools</li>
        </ul>
        <p>Data scientists:<sup>1</sup></p>
        <ul>
            <li>Have strong math and statistical skills</li>
            <li>Have a strong business acumen</li>
            <li>Have strong computer science / coding skills</li>
            <li>Identify trends with machine learning</li>
            <li>Make predictions based on data trends</li>
            <li>Write code to assist in data analysis</li>
        </ul>
        <p>Though data analysts and data scientists have different backgrounds and strengths, keep in mind that these
            roles can be a little squishy in how they’re defined. This means responsibilities may change depending on
            the organization.</p>
        <h2>Types of data analytics</h2>
        <p>At its core, data analytics is about answering questions and making decisions. And just as there are
            different types of questions, there are also different types of data analytics depending on what you’re
            hoping to accomplish. While there’s no set-in-stone glossary of these types of data analytics, the <a
                href="https://www.scnsoft.com/blog/4-types-of-data-analytics">folks at ScienceSoft</a> do an excellent
            job breaking this work down into four primary areas:<sup>2</sup></p>
        <ul>
            <li>Descriptive analytics answers, “What happened?”</li>
            <li>Diagnostic analytics answers, “Why did something happen?”</li>
            <li>Predictive analytics answers, “What is likely to happen?”</li>
            <li>Prescriptive analytics answers, “What action should be taken?”</li>
        </ul>
        <p>Data analysts can tailor their work and solution to fit the scenario. For instance, if a manufacturer is
            plagued with delays and unplanned stoppages, a diagnostic analytics approach could help identify what
            exactly is causing these delays. From there, other forms of analysis can be used for fixing these issues.
        </p>
        <h2>What tools do data analysts use?</h2>
        <p>Data analysts rely on various tools to collect and make sense of their data. Kendra’s team uses specialized
            tools to efficiently gather data from social media, news sites and magazines as well as tools to sort and
            categorize data to visualize that data for reports and presentations.</p>
        <p>These are some common tools in a data analyst’s tool belt:</p>
        <ul>
            <li>Microsoft Excel®</li>
            <li>SQL</li>
            <li>SAS® software</li>
            <li>Google Analytics ™</li>
            <li>Google Tag Manager</li>
            <li>Tableau™</li>
            <li>Google AdWords™</li>
        </ul>
        <h2>What’s in a data analyst job description?</h2>
        <p>All these job duties drill down to one main goal for data analysts: “By analyzing data, we hope to move our
            clients’ business forward based on their strategic goals,” Pearson says.</p>
        <p>Data that is collected without proper examination is worthless. A data analyst’s true job is to add value to
            a company—either their own or their clients’. “Through making the data we collect insightful and easy to
            understand, we provide additional value to our clients to help them make informed decisions for their
            business,” Pearson says.</p>
        <h2>Should you become a data analyst?</h2>
        <p>So what does a data analyst do? A lot more than you might think. Now that you know the teamwork, technical
            skills and strategizing that go into this up-and-coming profession, you may be considering a data analysis
            career yourself.</p>
        <p>We’ve covered the basics of the data analyst job description, but if you want to dive in deeper, check out
            our article, “<a href="https://www.rasmussen.edu/degrees/technology/blog/data-analyst-job-description/">5
                Insights You Won’t Find in the Data Analyst Job Description.</a>”</p>
        <p class="blog-post-disclosure"><sup>1</sup>Towards Data Science, “Blurred Lines: Data Analyst vs. Data
            Science,” [information accessed January 16, 2019] <a
                href="https://towardsdatascience.com/blurred-lines-data-analytics-vs-data-science-12ff92a3bd4e">https://towardsdatascience.com/blurred-lines-data-analytics-vs-data-science-12ff92a3bd4e</a><br/>
            <sup>2</sup>Sciencesoft, “4 Types of Data Analytics to Improve Decision-Making,” [information accessed
            January 16, 2019] <a
                href="https://www.scnsoft.com/blog/4-types-of-data-analytics">https://www.scnsoft.com/blog/4-types-of-data-analytics</a><br/>
            Google, Google AdWords and Google Analytics are trademarks of Google, Inc.<br/>
            SAS is a registered trademark of SAS Institute, Inc.<br/>
            Tableau is a registered trademark of Tableau Software.<br/>
            Microsoft Excel is a registered trademark of Microsoft, Inc.
        </p>
    </div>


</article>
        </div>
    )
}

export default DataAnal
